// Drží `document.title` = „{název instance} · {titulek aktivního okna}".
//
// Název instance = `getRuntimeConfig().ui.title` (per-deployment; dnes se nastavuje jednou při bootu
// v main.tsx). Aktivní okno = globálně fokusované (`windowManagerStore.activeLayerId`). Titulek okna se
// rozliší stejně jako v `deriveDesktopProjection`: native → `nativeWindowTitleStore`, legacy →
// `legacyWindowChromeStore`, fallback → `resolveTitle` (statický popisek typu). Mountuje se jednou v shellu.

import { useEffect } from 'react';

import {
  useNavigationContext,
  useNavigationStore,
} from '@/shared/navigation/react/useNavigationContext';
import { resolveTitle } from '@/shared/navigation/shell';
import { getRuntimeConfig } from '@/shared/runtimeConfig';

import { useWindowManagerStore } from '../core/windowManagerStore';
import { useNativeWindowTitleStore } from '../core/nativeWindowTitleStore';
import { useLegacyWindowChromeStore } from '../legacy/legacyWindowChromeStore';

export function useDocumentTitle(): void {
  const { kernel } = useNavigationContext();
  const layers = useNavigationStore((s) => s.navigation.layers);
  const activeLayerId = useWindowManagerStore((s) => s.activeLayerId);
  const nativeTitle = useNativeWindowTitleStore((s) =>
    activeLayerId ? s.byLayer[activeLayerId] : undefined
  );
  const chromeTitle = useLegacyWindowChromeStore((s) =>
    activeLayerId ? s.byLayer[activeLayerId]?.title : undefined
  );

  useEffect(() => {
    const instance = getRuntimeConfig().ui.title;
    const layer = activeLayerId ? layers.find((l) => l.layerId === activeLayerId) : undefined;
    let active = '';
    if (layer) {
      const config = kernel.getRegistry().getById(layer.agendaId);
      const kind = config?.presentationHints?.windowKind ?? 'native';
      active = (kind === 'legacy' ? chromeTitle : nativeTitle) || resolveTitle(layer, config);
    }
    const next = [instance, active].filter(Boolean).join(' · ');
    if (next) document.title = next;
  }, [kernel, layers, activeLayerId, nativeTitle, chromeTitle]);
}
